import axios from "axios";
import elliemaeApi from "./interceptors/elliemaeApi";

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;

/**
 * Fetches access token using authCode.
 * @param {string} authCode
 * @param {string} TPOUserId
 * @param {string} state
 * @returns {Promise<string|null>} accessToken
 */
export const getTPOContact = async (authCode, TPOUserId, state = "") => {
    const url = `${API_DOMAIN}/api/external-users/details`;
    try {
        const response = await axios.post(url, { AuthCode: authCode, ExternalUserId: TPOUserId, PropertyState: state }, {
            headers: { "Content-Type": "application/json" },
        });

        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error fetching token:", error);
        throw error;
    }
};

/**
 * Fetches access token using authCode.
 * @param {string} authCode
 * @param {string} LOUserId
 * @param {string} LPUserId
 * @returns {Promise<string|null>} accessToken
 */
export const getLOLPUser = async (authCode, LOUserId, LPUserId) => {
    const url = `${API_DOMAIN}/api/external-users/user-details`;
    try {
        const response = await axios.post(url, { AuthCode: authCode, ExternalLOUserId: LOUserId, ExternalLPUserId: LPUserId }, {
            headers: { "Content-Type": "application/json" },
        });

        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error fetching token:", error);
        throw error;
    }
};


/**
 * Fetches user details
 * @param {string} accessToken
 * @param {string} userId
 * @returns {Promise<Object|null>} User Object
 */
export const getUserDetails = async (accessToken, userId) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }
    if (!userId) {
        console.warn("No User ID provided.");
        return null;
    }

    const url = `/encompass/v3/externalUsers/${userId}`;
    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
        });

        if (response.status === 200) {
            const user = response.data;
            return user || null;
        } else {
            throw new Error(`Unexpected status or data: ${response.status}`);
        }
    } catch (error) {
        console.error("Error fetching User Details:", error.message);
        throw error;
    }
};

/**
 * POST /api/external-users/licenses
 * @param {string} authCode
 * @param {string} tpoOrgId  // TPO.X15
 * @param {string} tpoUserId // TPO.X62
 * @param {string} stateCode // field 14
 */
export const getExternalUserLicenses = async (authCode, tpoOrgId, tpoUserId, stateCode) => {
    const url = `${API_DOMAIN}/api/external-users/licenses`;
    try {
        const { data, status } = await axios.post(
            url,
            { authCode, tpoOrgId, tpoUserId, stateCode },
            { headers: { "Content-Type": "application/json" } }
        );
        if (status === 200) return data;
        throw new Error(`Unexpected status: ${status}`);
    } catch (err) {
        console.error("Error calling external-users/licenses:", err);
        // Fail-safe: when license check can’t complete, force validation
        return { error: true };
    }
};

/**
 * GET /api/external-users/licenses/state/{stateCode}
 * Returns the state license requirement object.
 */
export const getStateLicenseRequirement = async (stateCode) => {
    if (!stateCode) return null;
    const url = `${API_DOMAIN}/api/external-users/licenses/state/${encodeURIComponent(stateCode)}`;
    try {
        const { data, status } = await axios.get(url, {
            headers: { "Content-Type": "application/json" },
        });
        if (status === 200) return data;
        console.warn("Unexpected response fetching state requirement:", status);
        return null;
    } catch (err) {
        console.error("Error fetching state license requirement:", err);
        return null;
    }
};

/**
 * Normalize date -> midnight of local day for safe comparisons.
 */
function toDateOnly(d) {
    if (!d) return null;
    const dt = new Date(d);
    dt.setHours(0, 0, 0, 0);
    return dt;
}

/**
 * Company license validation (mirrors your C# logic)
 * - Must match state
 * - Selected === true
 * - Exempted === false (company has Exempted)
 * - LicenseStatus empty OR contains "Approved"
 * - Date rules: no dates => valid; start only => valid if start <= today; start+end => valid if start <= today <= end
 * - end-only => treat as invalid (conservative)
 */
function isValidCompanyLicense(lic, stateCode) {
    if (!lic) return false;
    if (!lic.stateAbbreviation || lic.stateAbbreviation.toLowerCase() !== stateCode.toLowerCase()) return false;
    if (!lic.selected) return false;
    //if (lic.exempted) return false;
    const status = lic.licenseStatus || "";
    if (status && !status.toString().toLowerCase().includes("approved")) return false;

    const today = toDateOnly(new Date());
    const start = toDateOnly(lic.startDate);
    const end = toDateOnly(lic.endDate);

    if (!start && !end) return true;
    if (start && !end) return start <= today;
    if (start && end) return start <= today && today <= end;

    // end without start -> invalid (conservative)
    return false;
}

/**
 * Loan Officer / ExternalUser license validation (LO licenses do NOT have Exempted)
 * - Must match state
 * - Selected === true
 * - LicenseStatus empty OR contains "Approved"
 * - Date rules same as company (no dates => valid; start-only => valid if start <= today; start+end => valid if today in range)
 * - end-only => invalid
 */
function isValidUserLicense(lic, stateCode) {
    if (!lic) return false;
    if (!lic.stateAbbreviation || lic.stateAbbreviation.toLowerCase() !== stateCode.toLowerCase()) return false;
    if (!lic.selected) return false;

    const status = lic.licenseStatus || "";
    if (status && !status.toString().toLowerCase().includes("approved")) return false;

    const today = toDateOnly(new Date());
    const start = toDateOnly(lic.startDate);
    const end = toDateOnly(lic.endDate);

    if (!start && !end) return true;
    if (start && !end) return start <= today;
    if (start && end) return start <= today && today <= end;

    return false;
}

/**
 * fetchCompanyLicenseOnly — calls Encompass to fetch company license info for the given state.
 * Returns a license object (trimmed) or null if none valid, or { error: true } on network/unexpected failures.
 */
export const fetchCompanyLicenseOnly = async ({ accessToken, tpoOrgId, stateCode }) => {
    if (!accessToken) {
        console.warn("fetchCompanyLicenseOnly: missing accessToken");
        return { error: true };
    }
    if (!tpoOrgId || !stateCode) {
        console.warn("fetchCompanyLicenseOnly: missing tpoOrgId/stateCode");
        return { error: true };
    }
    const url = `/encompass/v3/externalOrganizations/tpos?tpoId=${encodeURIComponent(tpoOrgId)}&entities=license`;

    try {
        const resp = await elliemaeApi.get(url, {
            headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" }
        });

        if (resp.status !== 200 || !resp.data) {
            console.warn("fetchCompanyLicenseOnly: unexpected response", resp.status);
            return { error: true };
        }
        const payload = Array.isArray(resp.data) ? resp.data[0] : resp.data;
        if (!payload) {
            console.warn("fetchCompanyLicenseOnly: empty payload");
            return { error: true };
        }

        const stateLicenseTypes = payload?.license?.stateLicenseTypes || [];
        const found = stateLicenseTypes.find(lic => isValidCompanyLicense(lic, stateCode));

        const lenderType = payload.license?.lenderType;

        if (!found) {
            return {
                licenseDetails: null,
                lenderType: lenderType
            };
        }

        return {
            licenseDetails: {
                StateAbbreviation: found.stateAbbreviation,
                Selected: !!found.selected,
                LicenseNumber: found.licenseNumber || null,
                IssueDate: found.issueDate || null,
                StartDate: found.startDate || null,
                EndDate: found.endDate || null,
                LicenseStatus: found.licenseStatus || null
            },
            lenderType: lenderType
        };
    } catch (err) {
        console.error("fetchCompanyLicenseOnly: error calling Encompass", err?.message || err);
        return { error: true };
    }
};

/**
 * fetchUserLicenseOnly — calls Encompass to fetch an external user's licenses and returns the valid one for the state.
 * Returns a license object (trimmed) or null if none valid, or { error: true } on failure.
 */
export const fetchUserLicenseOnly = async ({ accessToken, tpoUserId, stateCode }) => {
    if (!accessToken) {
        console.warn("fetchUserLicenseOnly: missing accessToken");
        return { error: true };
    }
    if (!tpoUserId || !stateCode) {
        console.warn("fetchUserLicenseOnly: missing tpoUserId/stateCode");
        return { error: true };
    }

    const url = `/encompass/v3/externalUsers/${encodeURIComponent(tpoUserId)}`;

    try {
        const resp = await elliemaeApi.get(url, {
            headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" }
        });

        if (resp.status !== 200 || !resp.data) {
            console.warn("fetchUserLicenseOnly: unexpected response", resp.status);
            return { error: true };
        }
        const licenses = resp.data?.licenses || [];
        const found = licenses.find(lic => isValidUserLicense(lic, stateCode));

        if (!found) return null;

        return {
            StateAbbreviation: found.stateAbbreviation,
            Selected: !!found.selected,
            LicenseNumber: found.licenseNumber || null,
            IssueDate: found.issueDate || null,
            StartDate: found.startDate || null,
            EndDate: found.endDate || null,
            LicenseStatus: found.licenseStatus || null
        };
    } catch (err) {
        console.error("fetchUserLicenseOnly: error calling Encompass", err?.message || err);
        return { error: true };
    }
};


// ================================
// Fetch TPO Organization (NMLS ID)
// ================================
export const getTPOOrganization = async (accessToken, companyId) => {
    if (!accessToken || !companyId) {
        console.warn("Missing token or companyId");
        return null;
    }

    const url = `/encompass/v3/externalOrganizations/tpos?tpoId=${companyId}`;

    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
        });

        if (response.status === 200 && Array.isArray(response.data)) {
            return response.data[0]?.basicInfo?.businessInformation?.nmlsId || null;
        }

        return null;
    } catch (error) {
        console.error("Error fetching TPO Organization:", error);
        throw error;
    }
};

// ===========================================
// Fetch BPC Program List (Internal API)
// ===========================================
export const getBPCPrograms = async (orgId) => {
    if (!orgId) {
        console.warn("Missing org ID for BPC Programs call");
        return [];
    }

    const url = `${API_DOMAIN}/api/external-users/licenses/programs/${orgId}`;

    try {
        const response = await axios.get(url, {
            headers: { "Accept": "*/*" }
        });

        if (response.status === 200 && Array.isArray(response.data)) {
            return response.data;
        }
        return [];
    } catch (error) {
        console.error("Error fetching BPC Programs:", error);
        return [];
    }
};

/**
 * Fetches TPO Settings (basicInfo) for an external org
 * GET /settings/externalOrganizations/tpos/{orgId}?entities=basicInfo
 *
 * @param {string} accessToken
 * @param {string|number} orgId  ExternalOrg entityId (e.g. 6415)
 * @returns {Promise<Object|null>}
 */
export const getTpoSettingsBasicInfo = async (accessToken, orgId) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }
    if (!orgId) {
        console.warn("No orgId provided.");
        return null;
    }

    const url = `/encompass/v3/settings/externalOrganizations/tpos/${orgId}?entities=basicInfo`;

    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                Accept: "application/json",
            },
        });

        if (response.status === 200) {
            return response.data || null;
        } else {
            throw new Error(`Unexpected status or data: ${response.status}`);
        }
    } catch (error) {
        console.error("Error fetching TPO Settings BasicInfo:", error.message);
        throw error;
    }
};

export const getTpoSettingsAllBasicInfo = async (accessToken, orgId) => {
    if (!accessToken) {
        console.warn("No Access Token Available.");
        return null;
    }
    if (!orgId) {
        console.warn("No orgId provided.");
        return null;
    }

    const url = `/encompass/v3/settings/externalOrganizations/tpos/${orgId}?entities=all`;

    try {
        const response = await elliemaeApi.get(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                Accept: "application/json",
            },
        });

        if (response.status === 200) {
            return response.data || null;
        } else {
            throw new Error(`Unexpected status or data: ${response.status}`);
        }
    } catch (error) {
        console.error("Error fetching TPO Settings BasicInfo:", error.message);
        throw error;
    }
};

export const getUserByUserId = async (accessToken, userId) => {
    if (!accessToken) return null;
    if (!userId) return null;

    const safeUserId = String(userId).replace(/'/g, "''");
    const filter = encodeURIComponent(`userId='${safeUserId}'`);

    const url = `/encompass/v3/users?isRecursive=true&entities=all&filter=${filter}`;

    const response = await elliemaeApi.get(url, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
        },
    });

    const users = response?.data;
    return Array.isArray(users) ? users[0] : null;
};

export const getSalesRepOrg = async (authCode, userId) => {
    const url = `${API_DOMAIN}/api/external-users/salesrep-org`;

    try {
        const response = await axios.post(
            url,
            {
                AuthCode: authCode,
                UserId: userId
            },
            {
                headers: { "Content-Type": "application/json" }
            }
        );

        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error fetching SalesRep org:", error);
        throw error;
    }
};

export const getSalesRepAssignment = async (authCode, userId, loNmlsId) => {
    const url = `${API_DOMAIN}/api/external-users/salesrep-assignment`;

    try {
        const response = await axios.post(
            url,
            {
                AuthCode: authCode,
                UserId: userId,
                LoNmlsId: loNmlsId || ""
            },
            {
                headers: { "Content-Type": "application/json" }
            }
        );

        if (response.status === 200) {
            return response.data;
        }

        throw new Error(`Unexpected status: ${response.status}`);
    } catch (error) {
        console.error("Error fetching SalesRep assignment:", error);
        throw error;
    }
};


export const getBrokerLoAeAssignment = async (authCode, loEmailId) => {
    const url = `${API_DOMAIN}/api/external-users/broker-lo-ae-assignment`;

    try {
        const response = await axios.post(
            url,
            {
                AuthCode: authCode,
                LoEmailId: loEmailId || ""
            },
            {
                headers: { "Content-Type": "application/json" }
            }
        );

        if (response.status === 200 || response.status === 204) {
            return response.data || null;
        }

        throw new Error(`Unexpected status: ${response.status}`);
    } catch (error) {
        if (error?.response?.status === 404) {
            return null;
        }

        console.error("Error fetching Broker LO AE assignment:", error);
        throw error;
    }
};

/**
 * fetchLoCompHistoryFlag — calls Encompass to check if loCompHistory exists for a TPO.
 * Returns: { hasHistory: boolean, useParentInfo: boolean, count: number } or { error: true }
 */
export const fetchLoCompHistoryFlag = async ({ accessToken, tpoId, isRecursive = true }) => {
    if (!accessToken) {
        console.warn("fetchLoCompHistoryFlag: missing accessToken");
        return { error: true };
    }
    if (!tpoId) {
        console.warn("fetchLoCompHistoryFlag: missing tpoId");
        return { error: true };
    }

    const url =
        `/encompass/v3/externalOrganizations/tpos` +
        `?entities=loComp` +
        `&tpoId=${encodeURIComponent(tpoId)}` +
        `&isrecursive=${isRecursive ? "true" : "false"}`;

    try {
        const resp = await elliemaeApi.get(url, {
            headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" }
        });

        if (resp.status !== 200 || !resp.data) {
            console.warn("fetchLoCompHistoryFlag: unexpected response", resp.status);
            return { error: true };
        }

        const payload = Array.isArray(resp.data) ? resp.data[0] : resp.data;
        if (!payload) {
            console.warn("fetchLoCompHistoryFlag: empty payload");
            return { error: true };
        }

        const useParentInfo = !!payload?.loComp?.useParentInfo;
        const count = Array.isArray(payload?.loComp?.loCompHistory) ? payload.loComp.loCompHistory.length : 0;

        return {
            hasHistory: count > 0,
            useParentInfo,
            count
        };
    } catch (err) {
        console.error("fetchLoCompHistoryFlag: error calling Encompass", err?.message || err);
        return { error: true };
    }
};

export const getTPOContactByOrg = async (authCode, TPOUserId, state = "", orgEntityId = "") => {
    const url = `${API_DOMAIN}/api/external-users/details-by-org`;

    const payload = {
        AuthCode: authCode,
        ExternalUserId: TPOUserId,
        PropertyState: state,
        OrgEntityId: orgEntityId
    };

    const response = await axios.post(url, payload, {
        headers: { "Content-Type": "application/json" },
    });

    if (response.status === 200) return response.data;
    throw new Error(`Unexpected status: ${response.status}`);
};


// ===========================================
// Fetch Page Field Visibility
// ===========================================
export const getPageFieldVisibility = async (orgId, pageName) => {
    if (!orgId || !pageName) {
        console.warn("Missing orgId or pageName for PageFieldVisibility call");
        return { hiddenFields: [], readOnlyFields: [] };
    }

    const url = `${API_DOMAIN}/api/pagefieldvisibility/org/${encodeURIComponent(orgId)}/page/${encodeURIComponent(pageName)}`;

    try {
        const response = await axios.get(url, {
            headers: { "Accept": "*/*" }
        });

        if (response.status === 200 && response.data) {
            return {
                hiddenFields: Array.isArray(response.data.hiddenFields) ? response.data.hiddenFields : [],
                readOnlyFields: Array.isArray(response.data.readOnlyFields) ? response.data.readOnlyFields : []
            };
        }

        return { hiddenFields: [], readOnlyFields: [] };
    } catch (error) {
        if (error?.response?.status === 404) {
            return { hiddenFields: [], readOnlyFields: [] };
        }

        console.error("Error fetching Page Field Visibility:", error);
        return { hiddenFields: [], readOnlyFields: [] };
    }
};



//===========================================
// Fetch internal user (for org search dropdowns)
//===========================================
export const getInternalUserContact = async (authCode) => {
    const url = `${API_DOMAIN}/api/internal-users/details`;
    try {
        const response = await axios.post(url, { AuthCode: authCode }, {
            headers: { "Content-Type": "application/json" },
        });

        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error(`Unexpected status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error fetching internal user contact:", error);
        throw error;
    }
};